import React, { ChangeEvent, useCallback, useMemo } from 'react';
import c from 'classnames';
import cn from 'classnames';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import DialogComponent from '@lyearn/site/app/modules/website/components/DialogComponent';
import { ClickAwayListener, Popper, PopperProps } from '@lyearn/ui';

import { popoverSize } from './style';
import { InputPopoverProps } from './types';

const POPOVER_HEIGHT = 120;
const POPOVER_WIDTH = 280;

const InputPopover: React.FC<InputPopoverProps> = (props) => {
  const {
    readOnly,
    setHotspotsState,
    hotspotsState,
    selectedHotspot,
    anchorEl,
    setSelectedHotspot,
    updateAttrs,
    width,
    height,
    allowTextUpdateOnly,
    renderPopoverContent,
    popper,
    containInAnchorEl,
  } = props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const newHotspots = [...hotspotsState];
      newHotspots[selectedHotspot!] = {
        ...newHotspots[selectedHotspot!],
        caption: event.target.value,
      };
      setHotspotsState(newHotspots);
      updateAttrs({ hotspots: newHotspots });
    },
    [hotspotsState, selectedHotspot, setHotspotsState, updateAttrs],
  );

  // If user clicks on the hotspot, we don't set selected hotspot to null. Click handler on
  // the hotspot will handle the scenario. This is need to perform the toggle behaviour.
  const handleSelectedHotspot = useCallback(
    (event?: MouseEvent | TouchEvent) => {
      const hotspotContainer = (event?.target as Element).closest('.hotspot-container');
      if (
        !hotspotContainer ||
        Number.parseInt(hotspotContainer.getAttribute('data-id') as string, 10) !== selectedHotspot
      ) {
        setSelectedHotspot(null);
      }
    },
    [selectedHotspot, setSelectedHotspot],
  );

  const onInputClose = useCallback(
    (event?: MouseEvent | TouchEvent) => {
      const lastSelectedHotspot = selectedHotspot;
      handleSelectedHotspot(event);
      if (!hotspotsState[lastSelectedHotspot!]?.caption) {
        const newHotspots = [...hotspotsState];
        newHotspots.splice(lastSelectedHotspot!, 1);
        setHotspotsState(newHotspots);
        setSelectedHotspot(null);
        updateAttrs({ hotspots: newHotspots });
      }
    },
    [
      handleSelectedHotspot,
      hotspotsState,
      selectedHotspot,
      setHotspotsState,
      setSelectedHotspot,
      updateAttrs,
    ],
  );

  // We don't want popover to overflow from the image.
  const placement = useMemo(() => {
    let yPos = 'start';
    let xPos = 'right';
    if (
      Number.isInteger(selectedHotspot) &&
      width &&
      hotspotsState[selectedHotspot!] &&
      POPOVER_WIDTH >= width * ((100 - hotspotsState[selectedHotspot!].x) / 100)
    ) {
      xPos = 'left';
    }

    if (
      Number.isInteger(selectedHotspot) &&
      height &&
      hotspotsState[selectedHotspot!] &&
      POPOVER_HEIGHT >= height * ((100 - hotspotsState[selectedHotspot!].y) / 100)
    ) {
      yPos = 'end';
    }

    return xPos + '-' + yPos;
  }, [height, hotspotsState, selectedHotspot, width]);

  const renderContent = useCallback(
    () =>
      renderPopoverContent ? (
        renderPopoverContent({
          id: Number.isInteger(selectedHotspot) ? hotspotsState[selectedHotspot!].id : undefined,
        })
      ) : (
        <textarea
          autoFocus
          className={cn(
            'body-short-03 h-full w-full resize-none p-16 text-text-paragraph outline-none',
            popper?.textarea,
          )}
          placeholder={popper?.placeholder ?? t('Lygo:HotspotInputPlaceholder')}
          readOnly={readOnly && !allowTextUpdateOnly}
          value={Number.isInteger(selectedHotspot) ? hotspotsState[selectedHotspot!]?.caption : ''}
          onChange={onInputChange}
        />
      ),
    [
      allowTextUpdateOnly,
      hotspotsState,
      onInputChange,
      popper?.placeholder,
      popper?.textarea,
      readOnly,
      renderPopoverContent,
      selectedHotspot,
      t,
    ],
  );

  if (isMobile) {
    return (
      <DialogComponent
        open={Number.isInteger(selectedHotspot)}
        onClose={readOnly ? handleSelectedHotspot : onInputClose}>
        {renderContent()}
      </DialogComponent>
    );
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseUp"
      onClickAway={readOnly ? handleSelectedHotspot : onInputClose}>
      <Popper
        anchorEl={anchorEl}
        className={c('mx-8 rounded-8 shadow-01', popper?.className ?? popoverSize)}
        container={containInAnchorEl ? anchorEl : undefined}
        open={Number.isInteger(selectedHotspot)}
        placement={placement as PopperProps['placement']}>
        {renderContent()}
      </Popper>
    </ClickAwayListener>
  );
};

export default InputPopover;
